import { FC, useEffect, useState } from "react";

import classes from "./ImagePopup.module.css";
import axios from "axios";
import { useSelector } from "react-redux";
import { userState } from "../../store/user";
import { ArtworkModelDocument } from "../gallery/Gallery";
import { getLang, i18n } from "../../i18n";

interface Props {
  popupImageID: string;
  setPopupImage: React.Dispatch<React.SetStateAction<string>>;
}

const ImagePopup: FC<Props> = ({ popupImageID, setPopupImage }) => {
  const lang = getLang();
  const t = i18n.imagePopup[lang];

  const API_URL = process.env.REACT_APP_API_URL;
  const user = useSelector(userState);
  const [art, setArt] = useState<ArtworkModelDocument | null>(null);
  const [screenIsLoading, setScreenIsLoading] = useState(true);
  const closePopup = () => {
    setPopupImage("");
  };
  const level = art ? art.level : 0;
  let stars = new Array(level);
  for (let i = 0; i < level; i++) {
    stars.push(true);
  }
  for (let i = level; i < 7; i++) {
    stars.push(false);
  }
  useEffect(() => {
    console.log("artwork popup use effect");
    const getArtworkByID = async () => {
      setScreenIsLoading(true);
      await axios
        .get(`${API_URL}/artworks/${popupImageID}`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        })
        .then((response) => {
          setArt(response.data.artwork);
          setScreenIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setScreenIsLoading(false);
        });
    };

    if (popupImageID) {
      getArtworkByID();
    }
  }, [popupImageID]);
  return (
    <div
      className={`${classes.fullscreen} ${popupImageID ? classes.visible : ""}`}
    >
      <div
        className={`${classes.popup} ${popupImageID ? classes.visible : ""}`}
      >
        {/* Close button */}
        <div className={classes.close} onClick={closePopup}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 23 23"
            fill="none"
          >
            <path
              d="M16.2353 6.76471L6.76471 16.2353M6.76471 6.76471L16.2353 16.2353"
              stroke="#50A7EA"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {/* Title */}
        <div className={classes.title}>
          {!screenIsLoading && art ? art.title : t.loading}
        </div>

        {/* Author */}
        <div className={classes.author}>
          {t.by} <b>{!screenIsLoading && art ? art.author : ""}</b>
        </div>

        {/* Image */}
        <div className={classes.image}>
          {!screenIsLoading && art && (
            <img src={art.imageAmazonS3URL} alt={art.title} title={art.title} />
          )}
        </div>

        {/* Description */}
        {!screenIsLoading && art?.description && (
          <div className={classes.description}>{art.description}</div>
        )}

        {/* Value & WinRate */}
        <div className={classes.half}>
          <div className={classes.halfItem}>
            <h2>{t.value}</h2>
            {!screenIsLoading && art ? (
              <div className={classes.count}>
                {art.value === 0 ? 0 : Math.round(art.value)} 💎
              </div>
            ) : (
              <div className={classes.count}>? 💎</div>
            )}
          </div>

          {!screenIsLoading &&
            art &&
            (art.silentVotesCount > 0 || art.battleVotesCount > 0) && (
              <div className={classes.halfItem}>
                <div className={classes.rate}>
                  <h2>{t.winRate}</h2>
                  <div className={classes.points}>
                    {art.silentVotesWinner + art.battleVotesWinner}/
                    {art.silentVotesCount + art.battleVotesCount}
                  </div>
                </div>
                <div className={classes.bar}>
                  <div
                    className={classes.barFilled}
                    style={{
                      width: `${
                        ((art.silentVotesWinner + art.battleVotesWinner) /
                          (art.silentVotesCount + art.battleVotesCount)) *
                        100
                      }%`,
                    }}
                  >
                    {(
                      ((art.silentVotesWinner + art.battleVotesWinner) /
                        (art.silentVotesCount + art.battleVotesCount)) *
                      100
                    ).toFixed(0)}
                    %
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Image Level */}
        <h2>{t.imageLevel}</h2>
        <div className={classes.level}>
          <div className={classes.stars}>
            {stars.map((star, idx) => (
              <svg
                key={idx}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 27 27"
                fill="none"
              >
                <path
                  d="M8.53379 17.014L7.67934 21.9738C7.67934 22.4869 7.67934 23 8.53379 23C8.83874 23 12.6351 20.7766 12.9769 20.6056C13.7972 20.1951 14.6858 20.4346 15.0276 20.6056C15.3694 20.7766 19.1657 23 19.4707 23C20.3251 23 20.3251 22.4869 20.3251 21.9738L19.4707 17.014C19.4707 16.8429 19.2998 16.3299 19.8125 15.8168L23.572 12.0541C23.9708 11.598 24.4265 10.8569 23.2302 10.6859L17.7572 9.83074C17.0737 9.65971 16.7319 8.97559 16.561 8.63354L14.6812 4.52883C14.1344 3.57107 13.542 4.12976 13.3141 4.52883L11.2635 8.63354C11.0926 8.97559 10.9217 9.65971 10.2381 9.83074L4.76975 10.6859C3.57353 10.8569 4.02923 11.598 4.42798 12.0541L8.18754 15.8168C8.7002 16.3299 8.53379 16.8429 8.53379 17.014Z"
                  fill={star ? "#3095dd" : "#d2e9fb"}
                />
              </svg>
            ))}

            {/* NFT SVG */}
            <svg
              className={classes.nft}
              xmlns="http://www.w3.org/2000/svg"
              shapeRendering="geometricPrecision"
              textRendering="geometricPrecision"
              imageRendering="optimizeQuality"
              fillRule="evenodd"
              clipRule="evenodd"
              viewBox="0 0 512 474.47"
            >
              <path
                d="M261.96 88.8l130.36 69.1c3.61 2.09 5.89 6.02 5.89 10.2v138.28c.02 2.29-.65 4.45-1.81 6.28-1.13 1.81-2.77 3.3-4.72 4.28l-129.81 68.79a11.695 11.695 0 0 1-5.96 1.55c-2.09-.02-4.12-.58-5.86-1.6l-130.36-69.1c-1.85-1.07-3.3-2.57-4.3-4.3l-.2-.39c-.91-1.71-1.39-3.61-1.39-5.51V168.1a11.6 11.6 0 0 1 1.8-6.28 11.96 11.96 0 0 1 4.72-4.28l129.82-68.8c1.86-1.06 3.92-1.56 5.96-1.55 2.09.03 4.12.58 5.86 1.61z"
                fill="#d2e9fb"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagePopup;
